<script setup lang="ts">
import { computed } from 'vue';
import type { DeviceCount } from '@/@types/device';
import type { Route } from '@/@types/gtfs-ressource';
import type { OnDemandRoute } from '@/@types/on-demand-route';
import BaseIcon from '@/components/ui/BaseIcon.vue';
import OnDemandTag from '@/components/ui/OnDemandTag.vue';
import RouteBadge from '@/components/ui/RouteBadge.vue';
import BookmarkButton from '@/components/ui/BookmarkButton.vue';
import type { OnDemandServiceState } from '@/libs/on-demand-helper';
import { AppRoute } from '@/libs/routing';

const props = defineProps<{
  route: Route | OnDemandRoute;
  deviceCount?: DeviceCount;
  /** Set on an on-demand route only, with the number of vehicles serving it. */
  onDemand?: { state: OnDemandServiceState; vehicleCount: number };
}>();

const to = computed(() =>
  props.onDemand
    ? { name: AppRoute.ON_DEMAND_ROUTE_DETAILED, params: { onDemandRouteId: props.route.route_id } }
    : { name: AppRoute.ROUTE_DETAILED, params: { routeId: props.route.route_id } },
);
</script>

<template>
  <router-link :to="to" class="route-item">
    <div class="route-item__details">
      <div v-if="onDemand" class="route-item__tags">
        <RouteBadge :route="route" />
        <OnDemandTag />
      </div>
      <span class="route-item__longname">
        <RouteBadge v-if="!onDemand" :route="route" class="route-item__badge" />

        {{ route.route_long_name }}
      </span>
      <template v-if="onDemand">
        <div v-if="onDemand.state === 'live'" class="route-item__status route-item__status--connected">
          <BaseIcon
            name="rss"
            :stroke-width="3"
            class="route-item__status-icon route-item__status-icon--realtime"
          />
          {{ $tc('onDemand.liveVehicles', onDemand.vehicleCount) }}
        </div>
        <div v-else class="route-item__status">
          {{ onDemand.state === 'noVehicle' ? $t('onDemand.noVehicle') : $t('onDemand.noServiceToday') }}
        </div>
      </template>
      <div v-else-if="deviceCount" class="route-item__status route-item__status--connected">
        <BaseIcon
          name="rss"
          :stroke-width="3"
          class="route-item__status-icon route-item__status-icon--realtime"
        />
        {{ $tc('routes.connectedVehicles', { count: deviceCount.count }) }}
      </div>
      <div v-else class="route-item__status">
        {{ $t('routes.noVehicle') }}
      </div>
    </div>
    <BookmarkButton :element="route" />
  </router-link>
</template>

<style lang="scss">
.route-item {
  align-items: center;
  display: flex;
  gap: $spacing-4;
  padding: $spacing-4 $spacing-5;

  &:active {
    background-color: $color-neutral-lower;
    border-radius: 16px;
  }

  &__details {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: $spacing-2;
    min-width: 0;
  }

  &__tags {
    align-items: center;
    display: flex;
    gap: $spacing-2;
    min-width: 0;
  }

  &__badge {
    align-self: flex-start;
    margin-right: $spacing-2;
  }

  &__longname {
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    color: $color-text-dark;
    display: -webkit-box;
    font: $label-medium;
    letter-spacing: $label-medium-spacing;
    overflow: hidden;
  }

  &__status {
    align-items: center;
    color: $color-neutral-higher;
    display: flex;
    font: $label-smaller;
    gap: 6px;
    letter-spacing: $label-smaller-spacing;
  }

  &__status-icon {
    font-size: 16px;

    &--realtime {
      color: $color-primary;
      transform: rotate(270deg);
    }
  }
}
</style>
